import { Facet } from '#~/control/movement/index.js';
import { locateProxy } from '#~/agent/utils.js';
import { useTrialCameraLocked } from '#~/api/billing/camera-access.js';
import { DataSTag, Sel } from '#~/stag/tag.js';
import { isControlDevice } from '#~/device/index.js';
import { Controls as DeviceSettingsControls } from '#~/room/components/image/settings/index.js';
import { DeviceRegistryInterfaceSectionType } from '@tryiris-ai/common.protojs/api/v1/device_settings';
// import { DynamicControls as DynamicDeviceSettingsControls } from '#~/room/components/image/settings/index.js';
import { useRoom } from '#~/room/hooks.js';
import { useIsDeviceGated } from '#~/room/use-gating.js';
import { Empty, Unsupported } from './contingency.jsx';
// Import { General } from './general/index.js';
import type { T_OptionalKeys } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { twMerge } from 'cn';
import { Gamepad } from './gamepad.jsx';
import { Move } from './move/index.js';
import { QuickShots } from './quick-shots/index.js';
import { Speed } from './speed/index.js';
import type { Props } from './types.js';

/** @internal */
const Inner = ({ className, deviceId, ...props }: Props): ReactElement => {
  return (
    <>
      <div className={twMerge('flex flex-col', className)} {...props}>
        {/* Gamepad currently doesn't render anything */}
        <Gamepad />
        <DeviceSettingsControls
          deviceId={deviceId}
          sectionType={DeviceRegistryInterfaceSectionType.FOCUS_CONTROL}
        />
        <QuickShots deviceId={deviceId} />
        <Speed deviceId={deviceId} facet={Facet.PanTilt} />
        <DeviceSettingsControls
          deviceId={deviceId}
          sectionType={DeviceRegistryInterfaceSectionType.CONTROL_TRAY}
        />
        <Speed deviceId={deviceId} facet={Facet.Zoom} />

        {/* TODO: check if we still would like this? */}
        {/* <DynamicDeviceSettingsControls deviceId={deviceId} majorToMinorCategoryMapping={settings} /> */}

        <Move deviceId={deviceId} />
        {/* <General deviceId={deviceId} /> */}
      </div>
    </>
  );
};

/** Interim guard. */
const Guard = ({ deviceId, ...props }: Props): ReactElement => {
  const isSupported = useRoom((µ) => isControlDevice(µ.devices[deviceId]));
  // Gating §6 — suppress the PTZ controls panel (Move / QuickShots / Speed /
  // Gamepad) over a transitory placeholder OR a committed device whose stream
  // auth is INVALID. A placeholder is not in `room.devices`, so `isSupported`
  // is already false for it; this `!gated` term is what additionally protects
  // the committed-but-INVALID case (whose proxy exists and IS control-capable).
  const gated = useIsDeviceGated(deviceId);
  const proxy = locateProxy(deviceId);
  const locked = useTrialCameraLocked(proxy?.roomId, proxy?.id, deviceId);
  if (locked)
    return (
      <div
        role='status'
        className='text-base-content/60 p-16'
        {...DataSTag(Sel.MainRoomRightTrayControls, 'camera-locked', deviceId)}
      >
        Camera locked
      </div>
    );

  return isSupported && !gated ? <Inner deviceId={deviceId} {...props} /> : <Unsupported />;
};

/** `<Controls>` panel component. */
export const Controls = ({ deviceId, ...props }: T_OptionalKeys<Props, 'deviceId'>): ReactElement =>
  deviceId ? <Guard deviceId={deviceId} {...props} /> : <Empty />;
